import React, { useState } from "react";
import { DesktopEntityTypeWithSystems } from "@/lib/supabase/supabaseTypes";
import { dateOlderThanSeconds } from "@/dentlab/src/lib/utils/utils-functions";
import { useDesktopsStore } from "@/dentlab/src/store/Desktops";
import {
	Monitor,
	Printer,
	Info,
	RefreshCw,
	Trash2,
	Loader2,
} from "lucide-react";
import { DateTimePicker } from "@/dentlab/src/components/ui/datetime-picker";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";
import {
	sendTestPrintJob,
	pollPrintResponse,
} from "@/dentlab/src/lib/utils/print-utils";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { useExportPdf } from "@/dentlab/src/hooks/useExportPdf";
import { DruckertestTemplate } from "@/dentlab/src/pdf-templates/pdf-templates/druckertest.template";

interface ConnectedDesktopsProps {
	desktopsWithSystems: DesktopEntityTypeWithSystems[];
}

function getInstallAt(desktop: DesktopEntityTypeWithSystems): Date | null {
	const postponeTo = desktop.postpone_update_to;
	const downloadedAt = desktop.downloaded_at;
	const iso =
		postponeTo ??
		(downloadedAt
			? new Date(
					new Date(downloadedAt).getTime() + 5 * 60 * 1000
				).toISOString()
			: null);
	return iso ? new Date(iso) : null;
}

type UpdateDisplayState =
	| { kind: "pending"; timeText: string }
	| { kind: "installing" };

function getUpdateDisplayState(
	desktop: DesktopEntityTypeWithSystems
): UpdateDisplayState {
	const installAt = getInstallAt(desktop);
	if (!installAt || installAt.getTime() > Date.now()) {
		const timeText = installAt ? formatInstallTime(installAt) : "";
		return { kind: "pending", timeText };
	}
	return { kind: "installing" };
}

function formatInstallTime(d: Date): string {
	const isToday =
		d.getDate() === new Date().getDate() &&
		d.getMonth() === new Date().getMonth() &&
		d.getFullYear() === new Date().getFullYear();
	const timeStr = d.toLocaleTimeString("de-CH", {
		hour: "2-digit",
		minute: "2-digit",
	});
	if (isToday) return ` — Installation um ${timeStr}`;
	const dateStr = d.toLocaleDateString("de-CH", {
		day: "2-digit",
		month: "2-digit",
	});
	return ` — Installation am ${dateStr} um ${timeStr}`;
}

export const ConnectedDesktops: React.FC<ConnectedDesktopsProps> = ({
	desktopsWithSystems,
}) => {
	const getPrinterCapabilities = useDesktopsStore(
		(state) => state.getPrinterCapabilities
	);
	const { exportPdf } = useExportPdf();
	const [testingPrinters, setTestingPrinters] = useState<
		Record<number, boolean>
	>({});

	function handleDeleteDesktop(desktopId: string) {
		useDesktopsStore.getState().softDeleteDesktop(desktopId);
	}

	function handleCompatModeChange(printerId: number, checked: boolean) {
		useDesktopsStore
			.getState()
			.updateDesktopSystem(printerId, { compat_mode: checked });
	}

	async function handleDriverTestPrint(printerId: number) {
		setTestingPrinters((prev) => ({ ...prev, [printerId]: true }));
		try {
			const result = await exportPdf({
				documentComponent: <DruckertestTemplate />,
				forcePageBreak: ".pdf-page-break",
			});
			if (!result.success || !result.blob) {
				showNotification({
					message: "Drucktest-Dokument konnte nicht erstellt werden",
					type: "error",
				});
				return;
			}
			const printResult = await sendTestPrintJob(
				{
					desktop_system_id: printerId,
					tray_number: null,
					paper_size: null,
					duplex: false,
					color_mode: null,
				},
				desktopsWithSystems,
				result.blob
			);
			if (printResult.success && printResult.requestId) {
				if (printResult.desktopOnline) {
					await pollPrintResponse(printResult.requestId);
				} else {
					showNotification({
						message:
							"Desktop ist offline — Drucktest wird gesendet sobald der Desktop online ist",
						type: "warning",
					});
				}
			}
		} finally {
			setTestingPrinters((prev) => ({ ...prev, [printerId]: false }));
		}
	}

	if (desktopsWithSystems.length === 0) {
		return (
			<div>
				<h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
					Verbundene Desktops
				</h2>
				<p className="text-sm text-gray-400">
					Keine Desktops verbunden
				</p>
			</div>
		);
	}

	return (
		<div>
			<h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
				Verbundene Desktops
			</h2>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
				{desktopsWithSystems.map((desktop) => {
					const isOnline = !dateOlderThanSeconds(
						new Date(desktop.last_active_at),
						60
					);
					const lastActiveText = isOnline
						? "Aktiv"
						: `Zuletzt aktiv: ${new Date(
								desktop.last_active_at
							).toLocaleString("de-CH")}`;
					const printers = desktop.desktop_systems.filter(
						(s) => s.type === 1
					);

					return (
						<div
							key={desktop.id}
							className="border rounded-lg p-4 bg-white"
							data-testid={`connected-desktop-card-${desktop.id}`}
						>
							<div className="flex items-center gap-3 mb-2">
								<div
									className={`w-2.5 h-2.5 rounded-full ${
										isOnline
											? "bg-green-500"
											: "bg-gray-300"
									}`}
								/>
								<div className="flex items-center gap-2">
									<Monitor className="h-4 w-4 text-gray-500" />
									<span className="font-medium text-sm">
										{desktop.name}
									</span>
									{desktop.app_version && (
										<span
											className="text-xs text-gray-400"
											data-testid={`desktop-app-version-${desktop.id}`}
										>
											v{desktop.app_version}
										</span>
									)}
								</div>
								<span className="text-xs text-gray-400 ml-auto">
									{lastActiveText}
								</span>
								<button
									onClick={() =>
										handleDeleteDesktop(desktop.id)
									}
									className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
									aria-label={`Desktop ${desktop.name} entfernen`}
									data-testid={`delete-desktop-${desktop.id}`}
								>
									<Trash2 className="h-3.5 w-3.5" />
								</button>
							</div>
							{printers.length > 0 && (
								<div className="flex flex-col gap-2 pl-5">
									{printers.map((printer) => {
										const caps = getPrinterCapabilities(
											printer.id
										);
										const hasCaps =
											caps &&
											(caps.paperSizes.length > 0 ||
												caps.trays.length > 0);
										return (
											<div
												key={printer.id}
												className="text-xs"
											>
												<div className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-t w-fit">
													<Printer className="h-3 w-3" />
													<span className="font-medium">
														{printer.name}
													</span>
													<button
														onClick={() =>
															useDesktopsStore
																.getState()
																.deleteDesktopSystem(
																	printer.id
																)
														}
														className="ml-1 p-0.5 text-gray-400 hover:text-red-500 rounded transition-colors"
														aria-label={`Drucker ${printer.name} entfernen`}
														data-testid={`delete-printer-${printer.id}`}
													>
														<Trash2 className="h-3 w-3" />
													</button>
												</div>
												{hasCaps ? (
													<div className="bg-gray-50 px-2 py-1.5 rounded-b rounded-tr border border-gray-100 space-y-0.5 text-gray-500">
														{caps.paperSizes
															.length > 0 && (
															<div>
																<span className="text-gray-400">
																	Papierformate:
																</span>{" "}
																{caps.paperSizes
																	.map(
																		(ps) =>
																			ps.name
																	)
																	.join(", ")}
															</div>
														)}
														{caps.trays.length >
															0 && (
															<div>
																<span className="text-gray-400">
																	Fächer:
																</span>{" "}
																{caps.trays
																	.map(
																		(t) =>
																			t.name
																	)
																	.join(", ")}
															</div>
														)}
														<div>
															<span className="text-gray-400">
																Duplex:
															</span>{" "}
															{caps.supportsDuplex
																? "Ja"
																: "Nein"}
															<span className="text-gray-400 ml-3">
																Farbe:
															</span>{" "}
															{caps.supportsColor
																? "Ja"
																: "Nein"}
														</div>
													</div>
												) : (
													<div className="bg-gray-50 px-2 py-1.5 rounded-b rounded-tr border border-gray-100 text-gray-400 flex items-center gap-1">
														<Info className="h-3 w-3 shrink-0" />
														Druckerinfo nicht
														verfügbar — Desktop muss
														online sein
													</div>
												)}
												<div className="flex items-center gap-2 mt-1 text-gray-500">
													<Checkbox
														checked={
															printer.compat_mode
														}
														onCheckedChange={(
															checked
														) =>
															handleCompatModeChange(
																printer.id,
																checked as boolean
															)
														}
														aria-label={`Treiber-Kompatibilitätsmodus für ${printer.name}`}
														data-testid={`compat-mode-checkbox-${printer.id}`}
													/>
													<span>
														Treiber-Kompatibilitätsmodus
													</span>
													<TooltipWrapper title="Umgeht Fehler mancher Druckertreiber (z.B. unlesbare Zeichen ab der zweiten Seite), indem die Seiten als Bild an den Drucker gesendet werden. Der Druck kann dadurch etwas langsamer sein.">
														<Info className="h-3 w-3 text-gray-400" />
													</TooltipWrapper>
													<button
														onClick={() =>
															handleDriverTestPrint(
																printer.id
															)
														}
														disabled={
															testingPrinters[
																printer.id
															]
														}
														className="ml-auto px-2 py-0.5 border border-gray-200 rounded hover:bg-gray-50 text-gray-600 disabled:opacity-50 flex items-center gap-1"
														aria-label={`Drucktest auf ${printer.name} starten`}
														data-testid={`driver-test-print-${printer.id}`}
													>
														{testingPrinters[
															printer.id
														] ? (
															<Loader2 className="h-3 w-3 animate-spin" />
														) : (
															<Printer className="h-3 w-3" />
														)}
														Drucktest
													</button>
												</div>
											</div>
										);
									})}
								</div>
							)}
							{desktop.update_version && (
								<UpdateSection desktop={desktop} />
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

const UpdateSection: React.FC<{
	desktop: DesktopEntityTypeWithSystems;
}> = ({ desktop }) => {
	const [pickerValue, setPickerValue] = useState<Date | undefined>(undefined);
	const displayState = getUpdateDisplayState(desktop);

	function handlePostpone(minutes: number) {
		const target = new Date(Date.now() + minutes * 60 * 1000);
		useDesktopsStore
			.getState()
			.postponeUpdate(desktop.id, target.toISOString());
	}

	function handleDateTimeChange(date: Date | undefined) {
		setPickerValue(date);
		if (date && date > new Date()) {
			useDesktopsStore
				.getState()
				.postponeUpdate(desktop.id, date.toISOString());
		}
	}

	if (displayState.kind === "installing") {
		return (
			<div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200 text-xs">
				<div className="flex items-center gap-1.5 text-blue-800 font-medium">
					<RefreshCw className="h-3 w-3 animate-spin" />
					Update v{desktop.update_version} wird installiert…
				</div>
				<div className="text-blue-600 mt-1">
					Seite neu laden um den aktuellen Status zu sehen.
				</div>
			</div>
		);
	}

	return (
		<div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200 text-xs">
			<div className="flex items-center gap-1.5 text-blue-800 font-medium">
				<RefreshCw className="h-3 w-3" />
				Update v{desktop.update_version}
				{displayState.timeText}
			</div>
			<div className="flex items-center gap-2 mt-1.5 flex-wrap">
				<button
					onClick={() => handlePostpone(15)}
					className="px-2 py-0.5 bg-white border border-blue-300 rounded hover:bg-blue-50 text-blue-700"
					data-testid={`postpone-15min-${desktop.id}`}
					aria-label={`Update für ${desktop.name} um 15 Minuten verschieben`}
				>
					+15 Min
				</button>
				<button
					onClick={() => handlePostpone(60)}
					className="px-2 py-0.5 bg-white border border-blue-300 rounded hover:bg-blue-50 text-blue-700"
					data-testid={`postpone-1h-${desktop.id}`}
					aria-label={`Update für ${desktop.name} um 1 Stunde verschieben`}
				>
					+1 Std
				</button>
			</div>
			<div className="mt-1.5">
				<DateTimePicker
					value={pickerValue}
					onChange={handleDateTimeChange}
					placeholder="Zeitpunkt wählen…"
					className="h-7 text-xs"
					data-testid={`postpone-datetime-${desktop.id}`}
					aria-label={`Update für ${desktop.name} auf bestimmte Zeit verschieben`}
				/>
			</div>
		</div>
	);
};
